/**
 * Auth API helpers — built on the shared <api> client.
 * Server-side validation happens in the auth service (Pydantic + Argon2id + HIBP).
 * These functions just round-trip the values.
 */
import { api } from './client';
import { setAccessToken, clearAccessToken } from '../auth/tokenStore';

export interface TokenResponse {
  access_token: string;
  token_type: 'Bearer';
  expires_in: number;
  csrf_token?: string;
}

export async function signup(payload: { email: string; password: string }): Promise<TokenResponse> {
  const res = await api<TokenResponse>('/v1/auth/signup', { method: 'POST', body: payload });
  setAccessToken(res.access_token, res.expires_in);
  return res;
}

export interface MfaRequiredResponse {
  error: 'mfa_required';
  challenge: string;
  expires_in: number;
}

export type LoginResult =
  | { kind: 'authenticated'; tokens: TokenResponse }
  | { kind: 'mfa_required'; challenge: string; expires_in: number };

function isMfaRequired(x: unknown): x is MfaRequiredResponse {
  return typeof x === 'object' && x !== null && 'error' in x && (x as { error: unknown }).error === 'mfa_required';
}

/**
 * Password step. If the account has TOTP enrolled, returns `mfa_required`
 * with a short-lived challenge — the caller must collect a TOTP code and
 * call `loginTotp(challenge, code)`. Otherwise the access token is stored
 * and we return `authenticated`.
 */
export async function login(payload: { email: string; password: string }): Promise<LoginResult> {
  const res = await api<TokenResponse | MfaRequiredResponse>('/v1/auth/login', {
    method: 'POST',
    body: payload,
  });
  if (isMfaRequired(res)) {
    return { kind: 'mfa_required', challenge: res.challenge, expires_in: res.expires_in };
  }
  setAccessToken(res.access_token, res.expires_in);
  return { kind: 'authenticated', tokens: res };
}

/** Second step of MFA login. Exchanges challenge + code for real tokens. */
export async function loginTotp(payload: {
  challenge: string;
  code?: string;
  backup_code?: string;
}): Promise<TokenResponse> {
  const res = await api<TokenResponse>('/v1/auth/login/totp', { method: 'POST', body: payload });
  setAccessToken(res.access_token, res.expires_in);
  return res;
}

// ── TOTP enrollment (post-auth, in Settings → Security) ────────────────────

export interface TotpStatus {
  enrolled: boolean;
  pending: boolean;
  unused_backup_codes: number;
}

export interface TotpSetup {
  secret: string;
  otpauth_uri: string;
  qr_data_url: string;
}

export const totpApi = {
  status: () => api<TotpStatus>('/v1/auth/totp/status'),
  setup: () => api<TotpSetup>('/v1/auth/totp/setup', { method: 'POST' }),
  enable: (code: string) =>
    api<{ codes: string[] }>('/v1/auth/totp/enable', { method: 'POST', body: { code } }),
  disable: (payload: { code?: string; backup_code?: string }) =>
    api<void>('/v1/auth/totp/disable', { method: 'POST', body: payload }),
  regenerateBackupCodes: (code: string) =>
    api<{ codes: string[] }>('/v1/auth/totp/backup-codes/regenerate', {
      method: 'POST',
      body: { code },
    }),
};

export async function logout(): Promise<void> {
  try {
    await api<void>('/v1/auth/logout', { method: 'POST' });
  } finally {
    clearAccessToken();
  }
}

export async function forgotPassword(email: string): Promise<void> {
  await api<void>('/v1/auth/forgot-password', { method: 'POST', body: { email } });
}

export function oauthLoginUrl(provider: 'google' | 'x' | 'instagram'): string {
  const base = import.meta.env.VITE_API_BASE ?? 'https://api.kidzio.io';
  return `${base}/v1/auth/oauth/${provider}/login`;
}
